<template>
  <div class="full-width">
    <div class="column justify-center items-center">
      <div class="col-12 text-center q-mb-lg text-weight-bold">
        <p>What type of vehicle are you interested in? Select multiple options as required.</p>
      </div>
      <div class="body-type-container row text-center justify-center q-mb-lg">
        <div class="q-mb-sm">Choose the vehicle body type.</div>
        <div class="col-12 body-type-group">
          <CheckboxButton
            class="bodyType-button q-ma-xs"
            :color="selectedBodyTypes[bodyType.value] ? 'secondary' : 'grey-4'"
            v-for="(bodyType, index) in bodyTypes"
            :key="index"
            v-model="selectedBodyTypes[bodyType.value]"
            outlined
            removeOpacity
            stack
            greyBorder
            @input="handleSubmitButtonDisabled"
          >
            <QIcon
              :name="checkMarkIcon"
              color="white"
              :style="`position: absolute; top: 5px; right: 5px; ${selectedBodyTypes[bodyType.value] ? 'display:block' : 'display:none'}`"
            />
            <component
              :is="bodyType.icon"
              :color="selectedBodyTypes[bodyType.value] ? 'white' : undefined"
              class="bodyType-icon"
              style="width: 90px; height: 46px"
            />
            <span :class="`bodyType-label text-weight-bolder ${!selectedBodyTypes[bodyType.value] && 'text-primary'}`.trim()">{{
              bodyType.label
            }}</span>
          </CheckboxButton>
        </div>
      </div>
      <div class="fuel-type-container row text-center justify-center q-mb-lg">
        <div class="q-mb-sm">Choose the vehicle fuel type.</div>
        <div class="fuel-type-group col-12 row justify-center">
          <CheckboxButton
            v-for="(fuelType, index) in fuelTypeOptions"
            :key="index"
            :color="selectedFuelTypes[fuelType.value] ? 'secondary' : 'grey-4'"
            v-model="selectedFuelTypes[fuelType.value]"
            outlined
            removeOpacity
            greyBorder
            :label="fuelType.label"
            @input="handleSubmitButtonDisabled"
          />
        </div>
      </div>
      <div v-if="makes && makes.length > 1" class="column text-center justify-center q-mb-lg" style="max-width: 650px; width: 100%">
        <div class="text-weight-bold q-mb-sm">What makes of vehicle are you interested in?</div>
        <div class="row body-type select-make" style="justify-content: center">
          <CustomExpansionItem class="col-12 q-mb-sm" label="Select the makes" v-model="isMakeOpen">
            <RefineSearchMultiCheckbox
              style="text-align: left"
              label=""
              :options="makes"
              v-model="selectedMakes"
              hideSearch
              dense
              two-columns-mobile
              @change="handleSubmitButtonDisabled"
            />
          </CustomExpansionItem>
        </div>
      </div>
      <div class="column items-center">
        <CalculatorButton
          style="width: 200px; height: 48px"
          :color="isSubmitDisabled ? undefined : 'primary'"
          outline
          rounded
          label="Next"
          hasChevronIcon
          :disabled="isSubmitDisabled"
          @click="onSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { QBtn, QSelect, QIcon } from 'quasar';
import CalculatorButton from '../../components/Calculator/CalculatorButton';
import CheckboxButton from '../../components/Calculator/CheckboxButton';
import RefineSearchMultiCheckbox from '../../components/Calculator/RefineSearch/RefineSearchMultiCheckbox';
import CustomExpansionItem from '../../components/Calculator/CustomExpansionItem';
import SedanIcon from '../../components/Calculator/Icon/BodyTypes/SedanIcon';
import VanIcon from '../../components/Calculator/Icon/BodyTypes/VanIcon';
import TruckIcon from '../../components/Calculator/Icon/BodyTypes/TruckIcon';
import SUVIcon from '../../components/Calculator/Icon/BodyTypes/SuvIcon';
import { evaCheckmarkCircle2Outline } from '@quasar/extras/eva-icons';
import { mapGetters, mapActions } from 'vuex';
import { sortBy } from 'lodash';
import { searchFilterTypesMixin } from '@/mixins/calculator';
import { formatWordsAsSentences } from '../../utils/calculator/wordFormatter';
import {
  GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
  GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR,
  GTM_EVENT_STAGE_CHOOSE_YOUR_VEHICLE,
  MAKES_SEPARATOR
} from '../../utils/constants';

export default {
  name: 'ChooseVehiclePage',
  mixins: [searchFilterTypesMixin],
  data() {
    return {
      isSubmitDisabled: true,
      selectedBodyTypes: {},
      selectedFuelTypes: {},
      selectedMakes: {},
      isMakeOpen: false,
      checkMarkIcon: evaCheckmarkCircle2Outline
    };
  },
  computed: {
    ...mapGetters({
      orgProfile: 'theme/getOrgProfile'
    }),
    makes() {
      return (
        sortBy(
          this.makesFromVehicleSearchConfig?.map((value, index) => ({
            id: index++,
            label: this.formatWordsAsSentences(value),
            value
          })),
          'label'
        ) || []
      );
    }
  },

  methods: {
    formatWordsAsSentences,
    async handleSubmit() {
      this.$router.push({ name: 'calculatorResults' });
    }
  },
  watch: {
    makesFromVehicleSearchConfig: {
      immediate: true,
      handler(newMakes) {
        const makes = Array.isArray(newMakes) ? newMakes : [];

        // seed selectedMakes with defaults (do NOT overwrite existing user picks)
        const seeded = makes.reduce((acc, make) => {
          acc[make] = this.selectedMakes?.[make] ?? false;
          return acc;
        }, {});

        this.selectedMakes = {
          ...seeded,
          ...this.selectedMakes // keep any existing keys (e.g. old selections)
        };
      }
    }
  },
  methods: {
    ...mapActions({
      setApploading: 'app/setAppLoading'
    }),
    filterResultsWithTrueValues(objValue) {
      if (objValue) {
        return Object.keys(objValue).filter((value) => objValue[value]);
      }

      return objValue;
    },
    handleClickFuelType(fuelType) {
      this.selectedFuelTypes[fuelType.value] = !this.selectedFuelTypes[fuelType.value];
    },
    handleSubmitButtonDisabled() {
      const isBodyTypeEmpty = !Object.keys(this.selectedBodyTypes).filter((bodyType) => {
        return this.selectedBodyTypes[bodyType];
      })?.length;
      const isFuelTypeEmpty = !Object.keys(this.selectedFuelTypes).filter((fuelType) => this.selectedFuelTypes[fuelType])?.length;
      const isMakesEmpty = !Object.keys(this.selectedMakes)?.filter((make) => this.selectedMakes[make])?.length;

      if (isBodyTypeEmpty && isFuelTypeEmpty && isMakesEmpty) {
        this.isSubmitDisabled = true;
      } else {
        this.isSubmitDisabled = false;
      }
    },
    onSubmit() {
      this.setApploading(true);
      const makes = this.filterResultsWithTrueValues(this.selectedMakes).map((make) => make.toUpperCase());
      const fuelTypes = this.filterResultsWithTrueValues(this.selectedFuelTypes);
      const bodyTypes = this.filterResultsWithTrueValues(this.selectedBodyTypes);

      const params = {
        ...(makes.length > 0 && { pair: makes.join(MAKES_SEPARATOR) }),
        ...(fuelTypes.length > 0 && { vehicleFueltype: fuelTypes.join(',') }),
        ...(bodyTypes.length > 0 && {
          vehicleBodytype: this.bodyTypes
            .filter((bodyType) => bodyTypes.some((selectedBodyType) => selectedBodyType.toLowerCase() == bodyType.label.toLowerCase()))
            .map((bodyType) => bodyType.value)
            .join(',')
        })
      };

      this.$gtm.trackEvent({
        event: GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
        stage: GTM_EVENT_STAGE_CHOOSE_YOUR_VEHICLE,
        data: {
          ...GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR,
          ...(makes.length > 0 && { make: makes.join('|') }),
          ...(fuelTypes.length > 0 && { fuel: fuelTypes.join(',') }),
          ...(bodyTypes.length > 0 && {
            type: this.bodyTypes
              .filter((bodyType) => bodyTypes.some((selectedBodyType) => selectedBodyType.toLowerCase() == bodyType.label.toLowerCase()))
              .map((bodyType) => bodyType.value)
              .join(',')
          })
        }
      });

      this.$router.push({
        name: 'calculatorResults',
        query: {
          ...this.$route.query,
          ...params
        }
      });
    }
  },
  components: {
    QBtn,
    QSelect,
    QIcon,
    CalculatorButton,
    CheckboxButton,
    RefineSearchMultiCheckbox,
    CustomExpansionItem,
    SedanIcon,
    VanIcon,
    TruckIcon,
    SUVIcon
  }
};
</script>

<style lang="sass">

.bodyType-button
  max-width: 150px
  max-height: 110px
  min-height: 110px

  width: 100%
  height: 100%
  .q-checkbox__label
    height: 100%
    width: 100%
  .q-btn.calculator-button
    height: 100%
    width: 100%
    .q-btn__wrapper
      .q-btn__content
        display:flex
        flex-flow: column wrap
.q-expansion-item__container
 .q-item
  text-align: left
  color: $grey-6
.fuel-type-container, .body-type-container
  max-width: 650px
  width: 100%
.fuel-type-group
  .q-checkbox .calculator-button.q-btn.q-btn--rectangle:not(.q-btn--rounded)
    border-radius: 0 0 0 0
    width: 160px

  .q-checkbox:first-child .q-btn.q-btn--rectangle:not(.q-btn--rounded)
    border-radius: var(--calculator-group-button-radius, 40px) 0 0 var(--calculator-group-button-radius, 40px)

  .q-checkbox:last-child .q-btn.q-btn--rectangle:not(.q-btn--rounded)
    border-radius: 0 var(--calculator-group-button-radius, 40px) var(--calculator-group-button-radius, 40px) 0
.linear-white
  --icon-gradient-color1: #FFFFFF
  --icon-gradient-color2: #FFFFFF
.select-make .q-item
  min-height: 36px
  padding: 6px 12px
@media(max-width: 659px)
  .body-type-container
    max-width: 359px
    .body-type-group
      .bodyType-label
        margin-top: 0

  .bodyType-button
    max-height: 84px
    min-height: 84px
  .fuel-type-container
    max-width: 470px
    .fuel-type-group
      .q-checkbox:first-child .q-btn.q-btn--rectangle:not(.q-btn--rounded)
        border-radius: 20px 0 0 0

      .q-checkbox:nth-child(2) .q-btn.q-btn--rectangle:not(.q-btn--rounded)
        border-radius: 0 20px 0 0

      .q-checkbox:nth-child(3) .q-btn.q-btn--rectangle:not(.q-btn--rounded)
        border-radius: 0 0 0 20px
      .q-checkbox:last-child .q-btn.q-btn--rectangle:not(.q-btn--rounded)
        border-radius: 0 0 20px 0

@media(max-width: 339px)
  .bodyType-button
    max-width: 130px
    max-height: 100px
    min-height: 80px

  .body-type-container
    .body-type-group
      margin-bottom: 55px
  .fuel-type-group
    .q-checkbox .calculator-button.q-btn.q-btn--rectangle:not(.q-btn--rounded)
      width: 120px
</style>
